/**
 * The `address` field type (spec-widget-mcp D-W26, §7): country-aware sub-fields submitted as ONE
 * structured value `{ country, line1, line2?, city, region?, postcode? }` — the submitSchema accepts
 * an object of strings. The country picks which parts are shown, what they are called and which are
 * required; a country without its own entry uses the default shape (no part beyond line 1 and city
 * required, since many countries have no postcode).
 */

/** Every part's locale key suffix (`widget.address_<labelKey>`) and English fallback. */
export const ADDRESS_LABELS = Object.freeze({
	country: ["country", "Country"],
	line1: ["line1", "Address line 1"],
	line2: ["line2", "Address line 2 (optional)"],
	city: ["city", "City"],
	town: ["town", "Town or city"],
	state: ["state", "State"],
	province: ["province", "Province"],
	county: ["county", "County"],
	region: ["region", "Region"],
	postcode: ["postcode", "Postal code"],
	zip: ["zip", "ZIP code"],
	ukPostcode: ["uk_postcode", "Postcode"],
	eircode: ["eircode", "Eircode (optional)"],
});

const part = (key, label, required) => ({ key, label, required });
const LINES = [part("line1", "line1", true), part("line2", "line2", false)];

/** Per-country shapes; anything else is `DEFAULT`. */
const FORMATS = {
	US: [...LINES, part("city", "city", true), part("region", "state", true), part("postcode", "zip", true)],
	CA: [...LINES, part("city", "city", true), part("region", "province", true), part("postcode", "postcode", true)],
	GB: [...LINES, part("city", "town", true), part("postcode", "ukPostcode", true)],
	IE: [...LINES, part("city", "town", true), part("region", "county", false), part("postcode", "eircode", false)],
	AU: [...LINES, part("city", "city", true), part("region", "state", true), part("postcode", "postcode", true)],
	UA: [...LINES, part("city", "city", true), part("region", "region", false), part("postcode", "postcode", true)],
	DE: [...LINES, part("postcode", "postcode", true), part("city", "city", true)],
	FR: [...LINES, part("postcode", "postcode", true), part("city", "city", true)],
	PL: [...LINES, part("postcode", "postcode", true), part("city", "city", true)],
	NL: [...LINES, part("postcode", "postcode", true), part("city", "city", true)],
	IT: [...LINES, part("postcode", "postcode", true), part("city", "city", true), part("region", "province", false)],
	ES: [...LINES, part("postcode", "postcode", true), part("city", "city", true), part("region", "province", false)],
};
const DEFAULT = [...LINES, part("city", "city", true), part("region", "region", false), part("postcode", "postcode", false)];

/** The sub-fields for a country code (upper-cased); unknown → the default shape. */
export const addressParts = (country) => FORMATS[String(country || "").toUpperCase()] || DEFAULT;

/** ISO 3166-1 alpha-2 codes offered in the country picker. */
export const COUNTRY_CODES = Object.freeze((
	"AD AE AF AG AL AM AO AR AT AU AZ BA BB BD BE BF BG BH BI BJ BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR " +
	"CU CV CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GM GN GQ GR GT GW GY HK HN HR HT HU ID IE " +
	"IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KR KW KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN " +
	"MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PS PT PW PY QA RO RS RW SA SB SC SD SE SG " +
	"SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VN VU WS XK YE ZA ZM ZW"
).split(" "));

/** Country names in the visitor's language, sorted; the code when the runtime has no name for it. */
export function countryOptions(lang = "en") {
	let names = null;
	try {
		names = new Intl.DisplayNames([lang], { type: "region" });
	} catch (_) { /* an old runtime: show codes */ }
	const nameOf = (code) => {
		try {
			return names?.of(code) || code;
		} catch (_) {
			return code;
		}
	};
	return COUNTRY_CODES.map((code) => ({ code, name: nameOf(code) })).sort((a, b) => a.name.localeCompare(b.name, lang));
}

const filled = (v) => typeof v === "string" && v.trim() !== "";

/** The required parts (country included) that are blank — empty when the address is complete. */
export function addressMissing(value) {
	const v = value && typeof value === "object" ? value : {};
	const missing = filled(v.country) ? [] : ["country"];
	return [...missing, ...addressParts(v.country).filter((p) => p.required && !filled(v[p.key])).map((p) => p.key)];
}

/** One readable line: the shown parts in order, then the country. */
export function formatAddress(value) {
	const v = value && typeof value === "object" ? value : {};
	const parts = addressParts(v.country).map((p) => v[p.key]).filter(filled);
	return [...parts, v.country].filter(filled).join(", ");
}
